import React, { FC, SyntheticEvent } from 'react'

import { useDispatch } from 'react-redux'
import styled, { css } from 'styled-components'

import { fetchUniNoticeAction } from 'actions/uninotice/uniNoticeAction'
import { ModerationStatus } from 'api/photo/photo.types'
import { PhotoStatusOverflowAlbums } from 'common/constants'
import { Colors } from 'common-constants/colors'
import { media } from 'components/presentational'
import { FlagMainPhotoSvg } from 'components/presentational/svg/FlagMainPhotoSvg'
import { IncognitoSmallWithBackgroundSvg } from 'components/presentational/svg/IncognitoSmallWithBackgroundSvg'
import { PhotoStatusBlockedSvg } from 'components/presentational/svg/PhotoStatusBlockedSvg'
import { PhotoStatusModeratingSvg } from 'components/presentational/svg/PhotoStatusModeratingSvg'
import { PhotoStatusPendingSvg } from 'components/presentational/svg/PhotoStatusPendingSvg'
import { PortraitIcon } from 'components/presentational/svg/PortraitIcon'
import { VerifiedSvg } from 'components/presentational/svg/VerifiedSvg'
import {
  photoBlockedStatusNoticeId,
  photoModerationStatusNoticeId,
  photoPendingStatusNoticeId,
} from 'components/uninotice/uniNoticeIdList'

const styleWrapper = css`
  margin: 0 5px;

  ${media.minimalSize`
    margin: 0;
    ${(props) => props.theme.marginLeft}: -5px;
  `};
`

const Wrapper = styled.div`
  ${styleWrapper};
`

const WrapperCursor = styled.div`
  ${styleWrapper};
  cursor: pointer;
`

const WrapperStatusIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  width: 40px;
`

const WrapperStatusSmallIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
`

export const Incognito: FC<{
  isCurrentUserIncognito: boolean
  onClick: () => void
}> = ({ isCurrentUserIncognito, onClick }) => {
  if (isCurrentUserIncognito) {
    return (
      <WrapperCursor
        data-name="current-user-incognito-action"
        onClick={onClick}
      >
        <IncognitoSmallWithBackgroundSvg />
      </WrapperCursor>
    )
  }

  return null
}

export const PhotoVerified: FC<{ isVerified?: boolean; uid?: string }> = ({
  isVerified,
  uid = 'Common',
}) => {
  if (isVerified) {
    return (
      <Wrapper data-name="photo-verified">
        <VerifiedSvg uid={`PhotoVerified-${uid}`} size={24} />
      </Wrapper>
    )
  }

  return null
}

export const DefaultPhoto: FC<{ isDefaultPhoto?: boolean }> = ({
  isDefaultPhoto,
}) => {
  if (isDefaultPhoto) {
    return (
      <Wrapper data-name="default-photo">
        <FlagMainPhotoSvg />
      </Wrapper>
    )
  }

  return null
}

export const PortraitPhoto: FC<{ uid: string; isPortraitPhoto?: boolean }> = ({
  uid,
  isPortraitPhoto,
}) => {
  if (isPortraitPhoto) {
    return (
      <Wrapper data-name="portrait-photo">
        <PortraitIcon uid={uid} />
      </Wrapper>
    )
  }

  return null
}

interface PhotoViewerPhotoStatusProps {
  status: string
  isFromPhotoViewer?: boolean
}

export const PhotoViewerPhotoStatus: FC<PhotoViewerPhotoStatusProps> = ({
  status,
  isFromPhotoViewer,
}) => {
  const dispatch = useDispatch()
  const handleClick = (nameOfNoticeId: string, event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    dispatch(fetchUniNoticeAction(nameOfNoticeId))
  }

  switch (status) {
    case PhotoStatusOverflowAlbums:
      if (isFromPhotoViewer) {
        return (
          <WrapperStatusIcon
            data-name="photo-status-overflow-action"
            onClick={(event) => handleClick(photoPendingStatusNoticeId, event)}
          >
            <PhotoStatusPendingSvg />
          </WrapperStatusIcon>
        )
      }

      return (
        <WrapperStatusSmallIcon
          data-name="photo-status-overflow-action"
          onClick={(event) => handleClick(photoPendingStatusNoticeId, event)}
        >
          <PhotoStatusPendingSvg color={Colors.link} />
        </WrapperStatusSmallIcon>
      )
    case ModerationStatus.rejected:
      if (isFromPhotoViewer) {
        return (
          <WrapperStatusIcon
            data-name="photo-status-blocked-action"
            onClick={(event) => handleClick(photoBlockedStatusNoticeId, event)}
          >
            <PhotoStatusBlockedSvg />
          </WrapperStatusIcon>
        )
      }

      return (
        <WrapperStatusSmallIcon
          data-name="photo-status-blocked-action"
          onClick={(event) => handleClick(photoBlockedStatusNoticeId, event)}
        >
          <PhotoStatusBlockedSvg color={Colors.warning} />
        </WrapperStatusSmallIcon>
      )
    case ModerationStatus.pending:
      if (isFromPhotoViewer) {
        return (
          <WrapperStatusIcon
            data-name="photo-status-moderation-action"
            onClick={(event) =>
              handleClick(photoModerationStatusNoticeId, event)
            }
          >
            <PhotoStatusModeratingSvg />
          </WrapperStatusIcon>
        )
      }

      return (
        <WrapperStatusSmallIcon
          data-name="photo-status-moderation-action"
          onClick={(event) => handleClick(photoModerationStatusNoticeId, event)}
        >
          <PhotoStatusModeratingSvg color={Colors.link} />
        </WrapperStatusSmallIcon>
      )
    default:
      return null
  }
}
